import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Layout, showToast } from '@components/Layout';
import OAuthConnectionStatus from './OAuthConnectionStatus';
import { showSpinner, hideSpinner } from '@components/Layout/LoadingSpinner';
import { trpc } from '@/api/trpc';
import { useTrpcMutation } from '@/api/useTrpcMutation';
import { useAuth } from '@/context/AuthContext';
import SupportRestrictedFeatureCard from '@/components/Support/SupportRestrictedFeatureCard';

const SUPPORTED_OAUTH_PROVIDERS = new Set(['tradestation', 'schwab', 'alpaca'] as const);
type OauthProviderId = 'tradestation' | 'schwab' | 'alpaca';
type OAuthCallbackLocationState = {
    oauthProcessing?: boolean;
    oauthResult?: 'success' | 'error';
    oauthErrorMessage?: string;
    oauthAccountAccessWarning?: string | null;
};

function isSupportedOauthProvider(id: string): id is OauthProviderId {
    return SUPPORTED_OAUTH_PROVIDERS.has(id as OauthProviderId);
}

function getErrorMessage(paramsObject: Record<string, string>) {
    if (paramsObject.error) {
        return paramsObject.error_description ? `${paramsObject.error}: ${paramsObject.error_description}` : paramsObject.error;
    }

    if (!paramsObject.code) return 'The brokerage provider did not return an authorization code.';
    if (!paramsObject.state) return 'The brokerage provider did not return a valid connection state.';

    return 'We could not complete the brokerage connection.';
}

const OAuthCallback: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const { isAuthenticated, sessionPolicy } = useAuth();
    const { canManageBrokerConnections } = sessionPolicy;

    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);
    const [accountAccessWarning, setAccountAccessWarning] = useState<string | null>(null);

    const paramsObject = useMemo(() => {
        const params: Record<string, string> = {};

        searchParams.forEach((value, key) => {
            params[key] = value;
        });

        return params;
    }, [searchParams]);

    const locationState = (location.state as OAuthCallbackLocationState | null) ?? null;
    const persistedSuccess = locationState?.oauthResult === 'success';
    const persistedError = locationState?.oauthResult === 'error' ? (locationState.oauthErrorMessage ?? null) : null;

    const saveOauthTokenMutation = useTrpcMutation(
        trpc.auth.saveOauthToken.mutationOptions({
            onSuccess: (data) => {
                setSuccess(true);
                setError(null);
                setAccountAccessWarning(data.accountAccessWarning);
                showToast(
                    data.accountAccessWarning ? 'Authorization saved; review your trading accounts.' : 'Brokerage authorization completed',
                    data.accountAccessWarning ? 'warning' : 'success'
                );

                if (id) {
                    void navigate(`/oauthCallback/${id}`, {
                        replace: true,
                        state: {
                            oauthResult: 'success',
                            oauthAccountAccessWarning: data.accountAccessWarning
                        } satisfies OAuthCallbackLocationState
                    });
                }
            },
            onError: (mutationError) => {
                const message =
                    mutationError?.message && typeof mutationError.message === 'string'
                        ? mutationError.message
                        : 'Failed to complete brokerage authorization';

                setError(message);

                if (id) {
                    void navigate(`/oauthCallback/${id}`, {
                        replace: true,
                        state: { oauthErrorMessage: message, oauthResult: 'error' } satisfies OAuthCallbackLocationState
                    });
                }
            }
        }),
        {
            fallbackErrorMessage: 'Failed to complete brokerage authorization'
        }
    );

    const isProcessing = saveOauthTokenMutation.isPending;

    useEffect(() => {
        if (isProcessing) {
            showSpinner();
            return;
        }

        hideSpinner();
    }, [isProcessing]);

    const processedRef = useRef<string | null>(null);

    useEffect(() => {
        if (!canManageBrokerConnections) return;
        if (processedRef.current) return;

        if (!id) return;

        if (paramsObject.error) {
            const message = getErrorMessage(paramsObject);
            showToast(message, 'error');
            return;
        }

        if (!paramsObject.code || !paramsObject.state) return;

        if (!isSupportedOauthProvider(id)) return;

        const processedKey = `${id}:${paramsObject.code}:${paramsObject.state}`;
        if (processedRef.current === processedKey) return;
        processedRef.current = processedKey;

        void navigate(`/oauthCallback/${id}`, {
            replace: true,
            state: { oauthProcessing: true }
        });

        saveOauthTokenMutation.mutate({
            service: id,
            code: paramsObject.code,
            state: paramsObject.state
        });
    }, [
        canManageBrokerConnections,
        id,
        navigate,
        paramsObject.code,
        paramsObject.error,
        paramsObject.error_description,
        paramsObject.state
    ]);

    const hasSucceeded = success || persistedSuccess;
    const resolvedAccountAccessWarning = hasSucceeded ? (accountAccessWarning ?? locationState?.oauthAccountAccessWarning ?? null) : null;

    const hasStartedProcessing =
        Boolean(locationState?.oauthProcessing) || Boolean(locationState?.oauthResult) || isProcessing || hasSucceeded || error !== null;

    const validationError = useMemo(() => {
        if (hasStartedProcessing) return null;
        if (!id) return 'We could not identify the brokerage provider for this connection.';
        if (paramsObject.error) return getErrorMessage(paramsObject);
        if (!isSupportedOauthProvider(id)) return 'This brokerage provider is not supported by the connection flow.';
        if (!paramsObject.code) return 'The brokerage provider did not return an authorization code.';
        if (!paramsObject.state) return 'The brokerage provider did not return a valid connection state.';
        return null;
    }, [hasStartedProcessing, id, paramsObject]);

    const resolvedError = error ?? persistedError ?? validationError;

    if (isAuthenticated && !canManageBrokerConnections) {
        return (
            <Layout hideHeaderUntilAuthenticated={false} title="Brokerage Connection">
                <SupportRestrictedFeatureCard
                    description="OAuth connection callbacks are disabled during a read-only support session because they can attach or refresh third-party account access."
                    title="Brokerage Connection Is Unavailable"
                />
            </Layout>
        );
    }

    return (
        <Layout hideHeaderUntilAuthenticated={false} title="Brokerage Connection">
            <OAuthConnectionStatus
                isProcessing={isProcessing}
                hasSucceeded={hasSucceeded}
                accountAccessWarning={resolvedAccountAccessWarning}
                errorMessage={resolvedError}
            />
        </Layout>
    );
};

export default OAuthCallback;
